import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, ClipboardList } from "lucide-react";
import SEO from "../components/SEO";
import { CALENDLY_URL, ALIGNMENT_CHECK_URL } from "../lib/site";

/* -------------------------------------------------------------------------
 *  Insight 002 — Consistency Creates Trust.
 *  Trust is often discussed as a relationship issue. In schools and
 *  districts, it is also a systems issue.
 *  Written under the Quiet Confidence Principle™.
 * ------------------------------------------------------------------------- */

export default function InsightConsistencyTrust() {
  return (
    <>
      <SEO
        title="Consistency Creates Trust | AlignED Insights"
        description="In schools and districts, trust is a systems issue as much as a relationship issue. People trust what they can depend on. Insight 002 from the AlignED library."
      />

      {/* Article Hero */}
      <section className="bg-[#FDFBF7] border-b border-[#0A192F]/10" data-testid="insight-hero">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 pt-20 pb-12 lg:pt-24 lg:pb-16">
          <Link
            to="/insights"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#52796F] hover:text-[#0A192F] transition-colors"
            data-testid="insight-back-link"
          >
            <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} /> Insights
          </Link>
          <div className="mt-8 text-xs uppercase tracking-[0.25em] text-[#38BDF8]">
            Insight 002 &middot; Consistent Execution&trade;
          </div>
          <h1 className="mt-4 font-serif-display text-4xl sm:text-5xl lg:text-[3.5rem] leading-[1.05] text-[#0A192F]">
            Consistency Creates Trust.
          </h1>
          <p className="mt-6 text-lg text-[#4A5568] leading-relaxed">
            Trust is often discussed as a relationship issue. In schools and districts, it is also a systems issue. People trust what they can depend on.
          </p>
        </div>
      </section>

      {/* Article Body */}
      <article className="bg-[#FDFBF7]" data-testid="insight-body">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 pb-24">
          <div className="prose-editorial text-[#1A1D20] leading-[1.75] text-lg space-y-7">

            <p>
              Trust is often discussed as a relationship issue. And it is. But in schools and districts, trust is also a systems issue. People trust what they can depend on.
            </p>

            <p>
              Students trust adults when expectations are clear and responses are consistent. Families trust schools when communication is timely and follow-through is reliable. Teachers trust leadership when priorities do not change every week. District leaders trust implementation when important work does not depend entirely on individual effort.
            </p>

            <p className="font-serif-display text-2xl sm:text-3xl text-[#0A192F] leading-snug border-l-2 border-[#38BDF8] pl-6 py-1">
              Consistency does not make schools mechanical. It makes them dependable. And dependability is one of the quiet foundations of trust.
            </p>

            <h2 className="font-serif-display text-3xl text-[#0A192F] leading-tight pt-8">
              Trust breaks where experience varies
            </h2>

            <p>
              Many schools have strong intentions. They have dedicated people, clear goals, and thoughtful plans. But the experience can still vary.
            </p>

            <p>
              One student receives timely attendance support. Another waits too long. One classroom reinforces expectations consistently. Another handles the same behavior differently. One family receives clear enrollment follow-up. Another is left unsure of the next step.
            </p>

            <p>
              In each case, the issue is not commitment. The issue is consistency.
            </p>

            <p>
              When experiences vary too widely, people begin to lose confidence in the system. Not always loudly. Often quietly.
            </p>

            <p>
              Students stop expecting follow-through. Families stop assuming communication will be clear. Staff stop believing priorities will last. Leaders stop trusting that plans are becoming practice.
            </p>

            <p>
              That is how trust erodes. Not always through one major failure. Often through small inconsistencies repeated over time.
            </p>

            <h2 className="font-serif-display text-3xl text-[#0A192F] leading-tight pt-8">
              Consistency is a leadership responsibility
            </h2>

            <p>
              Consistency does not happen because leaders mention expectations once. It happens because leaders build systems that make expectations visible, repeatable, and supported.
            </p>

            <p>
              That means leadership must pay attention to more than outcomes. Leadership must pay attention to the patterns that produce outcomes.
            </p>

            <ul className="space-y-3 pl-6 border-l-2 border-[#D4AF37]">
              <li className="text-[#1A1D20]">Where does follow-through depend on memory?</li>
              <li className="text-[#1A1D20]">Where does ownership become unclear?</li>
              <li className="text-[#1A1D20]">Where do teams interpret expectations differently?</li>
              <li className="text-[#1A1D20]">Where does communication vary by person, school, or week?</li>
              <li className="text-[#1A1D20]">Where is successful practice happening, but not spreading?</li>
            </ul>

            <p>
              These are not minor operational questions. They are trust questions. Because every unclear handoff, inconsistent response, or missed follow-up teaches people something about what the organization can be trusted to do.
            </p>

            <h2 className="font-serif-display text-3xl text-[#0A192F] leading-tight pt-8">
              Consistency does not mean sameness
            </h2>

            <p>
              There is an important distinction. Consistency does not mean every school, classroom, or team becomes identical. Schools need judgment. Students need individualization. Leaders need flexibility.
            </p>

            <p>
              But flexibility works best inside clear systems. A consistent system does not remove human judgment. It gives human judgment a dependable structure.
            </p>

            <p>
              For example, consistent attendance intervention does not mean every student receives the same support. It means every student receives dependable attention, visible follow-through, and a clear process for continued support.
            </p>

            <p>
              Consistent behavior practice does not mean every student receives the same response. It means adults share expectations, understand the response framework, and work from a common leadership rhythm.
            </p>

            <p>
              Consistent enrollment follow-through does not mean every family receives the same message. It means every family experiences timely communication, clear ownership, and a dependable next step.
            </p>

            <p className="font-serif-display text-xl sm:text-2xl text-[#52796F] leading-snug pt-2">
              That is the difference between rigid compliance and consistent leadership. AlignED is built around the second.
            </p>

            <h2 className="font-serif-display text-3xl text-[#0A192F] leading-tight pt-8">
              The trust pattern
            </h2>

            <p>
              Trust grows when people repeatedly experience alignment between what an organization says and what the organization does.
            </p>

            <p>
              If leadership says attendance matters, students should experience consistent support. If leadership says behavior expectations matter, adults should reinforce them consistently. If leadership says families matter, family communication should be dependable. If leadership says learning matters, successful practices should be captured and shared.
            </p>

            <div className="pt-2 space-y-2">
              <p className="font-serif-display text-2xl text-[#0A192F] leading-snug">Over time, consistency creates credibility.</p>
              <p className="font-serif-display text-2xl text-[#0A192F] leading-snug">Credibility creates confidence.</p>
              <p className="font-serif-display text-2xl text-[#0A192F] leading-snug">Confidence creates trust.</p>
              <p className="font-serif-display text-2xl text-[#52796F] leading-snug">And trust strengthens the conditions for improvement.</p>
            </div>

            <h2 className="font-serif-display text-3xl text-[#0A192F] leading-tight pt-8">
              A better leadership question
            </h2>

            <p>
              Many leadership teams ask:
            </p>

            <p className="font-serif-display text-2xl text-[#0A192F] leading-snug border-l-2 border-[#38BDF8] pl-6 py-1">
              &ldquo;How do we get people to trust the system?&rdquo;
            </p>

            <p>A better question is:</p>

            <p className="font-serif-display text-2xl text-[#0A192F] leading-snug border-l-2 border-[#38BDF8] pl-6 py-1">
              &ldquo;Where is the system giving people an inconsistent experience?&rdquo;
            </p>

            <p>
              That question changes the conversation. It moves leadership away from defending intentions and toward improving experience. It helps teams look at reality without blame. It invites leaders to examine whether the organization can consistently deliver what it believes.
            </p>

            <p>
              That is where meaningful improvement begins.
            </p>

            <h2 className="font-serif-display text-3xl text-[#0A192F] leading-tight pt-8">
              Closing reflection
            </h2>

            <p>
              Trust is not built by saying the right things. Trust is built when people experience the right things consistently.
            </p>

            <p>
              Every school wants to be trusted. Every district wants to be trusted. But trust becomes durable when leadership systems become dependable.
            </p>

            <div className="pt-2 space-y-2">
              <p className="font-serif-display text-2xl text-[#0A192F] leading-snug">Better leadership builds better systems.</p>
              <p className="font-serif-display text-2xl text-[#0A192F] leading-snug">Better systems create more consistent experiences.</p>
              <p className="font-serif-display text-2xl text-[#52796F] leading-snug">And consistent experiences create trust.</p>
            </div>

            <p className="pt-2">
              That is why consistency matters. Not because organizations need more control. Because students, families, and educators deserve systems they can depend on.
            </p>
          </div>

          {/* Contextual Invitation */}
          <div className="mt-20 pt-10 border-t border-[#0A192F]/10">
            <p className="text-base text-[#4A5568] leading-relaxed">
              If this perspective resonates with your leadership team, the next step is to look for where consistency may be drifting.
            </p>
            <p className="mt-3 text-base text-[#4A5568] leading-relaxed">
              Take the Alignment Check&trade; to reflect on visibility, ownership, follow-through, and execution across your organization.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <a
                href={ALIGNMENT_CHECK_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex justify-center items-center gap-2 bg-[#0A192F] text-[#FDFBF7] px-8 py-3.5 text-sm tracking-wide hover:bg-[#112240] transition-colors border border-[#0A192F]"
                data-testid="insight-cta-ac"
              >
                <ClipboardList className="w-4 h-4" strokeWidth={1.5} /> Take the Alignment Check&trade;
              </a>
              <a
                href={CALENDLY_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex justify-center items-center gap-2 text-[#0A192F] text-sm tracking-wide border-b border-[#0A192F] pb-1 self-start sm:self-center hover:text-[#52796F] hover:border-[#52796F] transition-colors"
                data-testid="insight-cta-walkthrough"
              >
                Schedule a Walkthrough <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </a>
            </div>
          </div>

          <div className="mt-16 flex justify-center">
            <Link
              to="/insights"
              className="inline-flex items-center gap-2 text-sm text-[#52796F] hover:text-[#0A192F] transition-colors border-b border-[#52796F]/40 hover:border-[#0A192F] pb-1"
              data-testid="insight-return-link"
            >
              <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Return to Insights
            </Link>
          </div>
        </div>
      </article>
    </>
  );
}
